import { AddToBasketESIWrapperUI } from "./AddToBasketESIWrapperUI";
import { StandardProps } from "../../componentMap";
import { useVariant } from "@/hooks/useVariant/useVariant";
import { isFutureRelease } from "@/utils/variant/isFutureRelease";
import { isInStoreOnly } from "@/utils/variant/isInStoreOnly";
import {
  ensureIsActive,
  ensureIsActiveVariant,
} from "@/utils/product/ensureIsActive";
import { useEffect, useState, useRef } from "react";
import {
  ADD_TO_BASKET_ESI_HIDDEN_OR_VISIBLE,
  ADD_TO_BASKET_ESI_SUCCESSFULLY_ADDED,
  ADD_TO_BASKET_ESI_WARNING_OR_ERROR,
  SIZE_SELECTOR_ERROR_NO_SIZE_SELECTED,
} from "@/constants/constants";
import { isAvailableToPreOrder } from "@/utils/variant/isAvailableToPreOrder";
import { hasAvailableVariantsMatchingColour } from "@/utils/product/hasAvailableVariantsMatchingColour";
import { getDefaultCategory } from "@/utils/product/getDefaultCategory";
import { getOwnBrand } from "@/utils/product/getOwnBrand";
import { isMadeToMeasure } from "@/utils/product/isMadeToMeasure";
import { isPerMetre } from "@/utils/product/isPerMetre";
import { sanitiseTitle } from "@/components/Title/sanitiseTitle";
import { dispatchAnalyticsEvent } from "@/utils";
import { hasExtendedWarranties } from "@/components/AddToBasket/utils/utils";

export function AddToBasketESIWrapper({
  product,
  configProps,
  runtimeConfig,
  flags,
}: Pick<StandardProps, "product" | "configProps" | "runtimeConfig" | "flags">) {
  const activeProduct = ensureIsActive(product);
  const { variant, isSelected } = useVariant(product);
  const activeVariant = ensureIsActiveVariant(variant);
  const [userAcknowledgementChecked, setUserAcknowledgementChecked] =
    useState<boolean>(false);
  const recsPlacementId =
    typeof configProps?.recsPlacementId === "string"
      ? configProps?.recsPlacementId
      : "OSPStandardA2BClick";
  const isShipFromStore = variant.availability?.shipFromStore === true;
  const expressCheckoutEnabled = configProps?.expressCheckoutEnabled === true;
  const addToBasketButtonEnabled =
    configProps?.addToBasketButtonEnabled !== false;
  const isShipFromStoreVariant = isShipFromStore && isSelected;

  const availableToOrder = !!activeVariant?.availability.availableToOrder;

  const customerNotifiableEvents = activeVariant?.customerNotifiableEvents;

  const showWishlistIconButtonVariant =
    String(flags.showWishlistIconButtonVariant.value) || "c";

  const isNotifiable =
    !availableToOrder &&
    (activeVariant?.customerNotifiableEvents.backInStock ||
      activeVariant?.customerNotifiableEvents.onRelease);

  const hasAvailableVariantsMatchingCurrentColour =
    hasAvailableVariantsMatchingColour(
      product,
      activeVariant?.differentiators?.colour,
    );

  const isInStoreOnlyVariant = isInStoreOnly(variant);
  const isFutureReleaseVariant = isFutureRelease(variant);

  const userAcknowledgementRequired =
    (!isInStoreOnlyVariant &&
      (product?.termsAndConditions?.customerConfirmationRequired ||
        isMadeToMeasure(product) ||
        isPerMetre(product))) ||
    false;

  const shouldHideAddToBasket =
    (userAcknowledgementRequired && !userAcknowledgementChecked) ||
    (isSelected &&
      (isInStoreOnlyVariant || isNotifiable || isFutureReleaseVariant));

  const shouldEnableAddToBasket = isSelected
    ? availableToOrder
    : hasAvailableVariantsMatchingCurrentColour;

  const appleProduct = product?.brand?.name.toLowerCase() === "apple";
  const variantHasExtendedWarranties = hasExtendedWarranties(variant?.services);

  const shouldScrollToA2B =
    appleProduct || variantHasExtendedWarranties
      ? true
      : flags.scrollToA2B.value === true;

  const a2bRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const eventDetail = {
      detail: {
        skuId: activeVariant?.aliases.skuId,
        variantId: isSelected ? activeVariant?.id : "",
        title: activeVariant?.title,
        price: activeVariant?.price?.value,
        enabled: String(shouldEnableAddToBasket),
        hidden: String(shouldHideAddToBasket),
        childItems: [],
        enableExpress: String(isShipFromStoreVariant || expressCheckoutEnabled),
        isShipFromStore: String(isShipFromStoreVariant),
        showAddToBasket: String(!isShipFromStore && addToBasketButtonEnabled),
      },
    };

    const event = new CustomEvent(
      "ADD_TO_BASKET_ESI:VARIANT_UPDATED",
      eventDetail,
    );

    dispatchEvent(event);
  }, [
    availableToOrder,
    customerNotifiableEvents,
    activeVariant?.price,
    isSelected,
    activeVariant?.aliases.skuId,
    activeVariant?.title,
    activeVariant?.id,
    userAcknowledgementChecked,
  ]);

  useEffect(() => {
    const handleAddToBasketError = (event: Event) => {
      const { errors = [] } = (event as CustomEvent).detail;
      const hasVariantError = errors.some(
        ({ code }: { code: string }) => code === "no-variant",
      );

      if (hasVariantError) {
        window.dispatchEvent(
          new CustomEvent(SIZE_SELECTOR_ERROR_NO_SIZE_SELECTED),
        );
      } else {
        if (shouldScrollToA2B) {
          document.getElementById("product:basket:a2besi")?.scrollIntoView({
            behavior: "smooth",
            inline: "nearest",
          });
        }
      }
    };
    window.addEventListener(
      ADD_TO_BASKET_ESI_WARNING_OR_ERROR,
      handleAddToBasketError,
    );
    return () =>
      window.removeEventListener(
        ADD_TO_BASKET_ESI_WARNING_OR_ERROR,
        handleAddToBasketError,
      );
  }, []);

  useEffect(() => {
    const handleUserAcknowledgement = (event: Event) => {
      const { userAcknowledgementChecked } = (event as CustomEvent).detail;
      setUserAcknowledgementChecked(userAcknowledgementChecked);
    };

    window.addEventListener(
      ADD_TO_BASKET_ESI_HIDDEN_OR_VISIBLE,
      handleUserAcknowledgement,
    );

    return () => {
      window.removeEventListener(
        ADD_TO_BASKET_ESI_HIDDEN_OR_VISIBLE,
        handleUserAcknowledgement,
      );
    };
  }, []);

  useEffect(() => {
    const scrollToAddToBasketButton = () => {
      if (a2bRef.current) {
        const scrollOptions: ScrollIntoViewOptions = {};
        if (!window.matchMedia("(prefers-reduced-motion)").matches) {
          scrollOptions.behavior = "smooth";
        }

        const scrollIntoView = () =>
          a2bRef.current?.scrollIntoView(scrollOptions);
        setTimeout(scrollIntoView, 0);
      }
    };

    if (shouldScrollToA2B) {
      window.addEventListener(
        ADD_TO_BASKET_ESI_SUCCESSFULLY_ADDED,
        scrollToAddToBasketButton,
      );
    }

    return () => {
      if (shouldScrollToA2B) {
        window.removeEventListener(
          ADD_TO_BASKET_ESI_SUCCESSFULLY_ADDED,
          scrollToAddToBasketButton,
        );
      }
    };
  }, [shouldScrollToA2B]);

  useEffect(() => {
    if (!isShipFromStore && expressCheckoutEnabled) {
      dispatchAnalyticsEvent(
        "global.impression",
        {
          type: "pdp",
          subType: "express checkout",
          additionalInfo: {},
          properties: {},
          products: [
            {
              brand: product.brand?.name,
              category: getDefaultCategory(product),
              sku: activeVariant?.aliases.skuId,
              price: activeVariant?.price?.value,
              productName: sanitiseTitle(variant?.title, getOwnBrand(product)),
              productId: activeProduct?.aliases.pimProductId,
              colour: activeVariant?.differentiators?.colour,
              size: activeVariant?.differentiators?.size,
            },
          ],
        },
        {},
      );
    }
  }, [variant]);

  if (!activeProduct || !activeVariant) return null;

  const isPreOrderableVariant = isAvailableToPreOrder(activeVariant);
  const isSingleSkuProduct = product.variants.length === 1;

  if (
    isSingleSkuProduct &&
    !availableToOrder &&
    !isFutureReleaseVariant &&
    !isPreOrderableVariant
  ) {
    return null;
  }

  return (
    <AddToBasketESIWrapperUI
      pimProductId={activeProduct.aliases.pimProductId}
      skuId={activeVariant.aliases.skuId}
      variantId={activeVariant.id}
      title={activeVariant.title}
      price={activeVariant.price?.value}
      availableToOrder={availableToOrder}
      recsPlacementId={recsPlacementId}
      hideAddToBasket={shouldHideAddToBasket}
      sendVariantIdToBasket={isSelected}
      shouldEnableAddToBasket={shouldEnableAddToBasket}
      expressCheckoutEnabled={isShipFromStoreVariant || expressCheckoutEnabled}
      showAddToBasketButton={
        !isShipFromStoreVariant && addToBasketButtonEnabled
      }
      shipFromStore={isShipFromStoreVariant}
      stockApiBaseUrl={runtimeConfig.stockApiBaseUrl}
      stockApiKey={runtimeConfig.stockApiKey}
      ref={a2bRef}
      showWishlistIconButtonVariant={showWishlistIconButtonVariant}
    />
  );
}
